import { UserRole } from '@cc/schema';
import { useCustom } from '@refinedev/core';
import { useMemo } from 'react';

export type PracticeMember = {
  _id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

/**
 * M7 · the practitioners of the caller's own practice — the options an "assign to" picker offers.
 *
 * ─── WHY IT IS NOT A `useSelect` ON THE `practiceUsers` RESOURCE ───────────────────────────────
 * That resource carries the roster page's own `permanent` filter and its own pagination, and
 * borrowing it would couple a picker to a table. This asks its own question, with the one filter
 * that makes the answer correct.
 *
 * ─── THE FILTER IS THE SAME DEFECT AS THE ROSTER'S (founder Q18) ───────────────────────────────
 * `practiceUsers` filters on `practiceId` and nothing else, and `createNonAppClient` writes CLIENT
 * rows carrying the practice's `practiceId` with role `NON_MOBILE` (5). Unfiltered, this picker
 * would offer "transfer this client to… that other client". The range `2 ≤ role ≤ 3` is the
 * practitioner roles and nothing else.
 *
 * `role_gte` / `role_lte` rather than `role_in`: the portal's `where` builder interpolates an `in`
 * value straight into AQL, so an array arrives as `2,3` and the query is malformed. Over a
 * contiguous enum the range says exactly the same thing and is emitted as real comparisons.
 */
export const usePracticeMembers = (options?: { enabled?: boolean }) => {
  const { data, isFetching } = useCustom<{
    practiceUsers: { data: PracticeMember[]; count: number };
  }>({
    url: '',
    method: 'get',
    meta: {
      operation: 'practiceUsers',
      fields: [{ data: ['_id', 'firstName', 'lastName', 'email'] }, 'count'],
      variables: {
        where: {
          name: 'where',
          type: 'JSON',
          value: {
            role_gte: UserRole.THERAPIST,
            role_lte: UserRole.THERAPIST_ADMIN,
          },
        },
        limit: { name: 'limit', type: 'Int', value: 200 },
      },
    },
    queryOptions: { enabled: options?.enabled ?? true },
  });

  const members = useMemo<PracticeMember[]>(() => {
    const rows = (data as any)?.data?.data ?? [];
    return Array.isArray(rows) ? rows.filter(Boolean) : [];
  }, [data]);

  return { members, isLoading: isFetching };
};

/** A person's name as a picker should show it, never a row key. */
export const practiceMemberLabel = (member: PracticeMember): string =>
  [member.firstName, member.lastName].filter(Boolean).join(' ')
  || member.email
  || member._id;
